import {
  getProfile as apiGetProfile,
  getMarketContext as apiGetMarketContext,
  sendClaudeAnalysis as apiSendClaudeAnalysis,
  sendOpenaiAnalysis as apiSendOpenaiAnalysis,
  fetchRiskAssessment as apiFetchRiskAssessment,
} from './services/apiService';
import { classifyNetworkError } from './utils/errorHandler';
import { withRetry } from './utils/retry';
import { log } from './utils/logging';
import { getETNow, getETDateString } from './utils/marketUtils';
import { AI_PROVIDER } from './constants/ai';

const API_KEY = process.env.REACT_APP_POLYGON_API_KEY;

if (!API_KEY) {
  console.error('REACT_APP_POLYGON_API_KEY is not set in the environment variables');
}

const BASE_URL = 'https://api.polygon.io/v3/reference/tickers';

// Polygon HTTP wrapper: reuses the shared withRetry algorithm. Default
// 5 retries / 1s / ×2 backoff suits Polygon's remote latency and the
// free-tier rate limits (occasional 429s clear after backoff).
const fetchWithRetry = async (url, options = {}, retries = 5, delay = 1000) =>
  withRetry(async (attempt) => {
    if (attempt > 0) console.debug(`API call attempt ${attempt + 1}...`);
    const response = await fetch(url, options);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  }, { retries, delayMs: delay, backoff: 2 });

export const fetchStockDetails = async (symbol) => {
  try {
    const data = await fetchWithRetry(`${BASE_URL}/${symbol}?apiKey=${API_KEY}`);
    if (data.status === 'OK' && data.results) {
      return {
        symbol: data.results.ticker,
        name: data.results.name,
        marketCap: data.results.market_cap,
        employees: data.results.total_employees,
        sicCode: data.results.sic_code || null,
        sicDescription: data.results.sic_description || 'N/A'
      };
    }
    throw new Error(`API returned invalid data for ${symbol}`);
  } catch (error) {
    console.error(`Error fetching details for ${symbol}:`, error.message);
    return null;
  }
};

export const fetchLastTrade = async (symbol) => {
  try {
    const data = await fetchWithRetry(`https://api.polygon.io/v2/last/trade/${symbol}?apiKey=${API_KEY}`);
    if (data.status === 'OK') {
      return {
        price: data.results.p,
        timestamp: data.results.t,
      };
    }
    throw new Error(`API returned non-OK status: ${data.status}`);
  } catch (error) {
    console.error(`Error fetching last trade data for ${symbol}:`, error.message);
    return null;
  }
};

export const fetchPreviousClose = async (symbol) => {
  try {
    const data = await fetchWithRetry(`https://api.polygon.io/v2/aggs/ticker/${symbol}/prev?adjusted=true&apiKey=${API_KEY}`);
    if (data.status === 'OK' && data.results && data.results.length > 0) {
      const result = data.results[0];
      return {
        open: result.o,
        high: result.h,
        low: result.l,
        close: result.c,
        volume: result.v,
      };
    }
    throw new Error(`API returned invalid data: ${JSON.stringify(data)}`);
  } catch (error) {
    console.error(`Error fetching previous close data for ${symbol}:`, error.message);
    return null;
  }
};

export const fetchMarketStatus = async () => {
  try {
    // Add timestamp to prevent browser caching
    const data = await fetchWithRetry(
      `https://api.polygon.io/v1/marketstatus/now?apiKey=${API_KEY}&_t=${Date.now()}`
    );
    return data;
  } catch (error) {
    console.error('Error fetching market status:', error.message);
    return null;
  }
};

// Fetch single ticker snapshot (returns daily change directly)
export const fetchTickerSnapshot = async (symbol) => {
  try {
    // Add timestamp to prevent browser caching
    const data = await fetchWithRetry(
      `https://api.polygon.io/v2/snapshot/locale/us/markets/stocks/tickers/${symbol}?apiKey=${API_KEY}&_t=${Date.now()}`
    );
    if (data.status === 'OK' && data.ticker) {
      return {
        price: data.ticker.day?.c || data.ticker.prevDay?.c,
        change: data.ticker.todaysChangePerc || 0
      };
    }
    return null;
  } catch (error) {
    console.error(`Error fetching snapshot for ${symbol}:`, error.message);
    return null;
  }
};

// Fetch batch ticker snapshots (for AI analysis - returns OHLC data)
// Uses Polygon's batch endpoint to get multiple tickers in one call
export const fetchBatchSnapshots = async (symbols) => {
  if (!symbols || symbols.length === 0) return {};

  try {
    // Polygon batch snapshot endpoint with ticker filter
    // Max ~100 tickers per request recommended
    const tickerList = symbols.join(',');
    const data = await fetchWithRetry(
      `https://api.polygon.io/v2/snapshot/locale/us/markets/stocks/tickers?tickers=${tickerList}&apiKey=${API_KEY}&_t=${Date.now()}`
    );

    if (data.status === 'OK' && data.tickers) {
      // Convert array to map for easy lookup
      const snapshotMap = {};
      for (const ticker of data.tickers) {
        const todayVolume = ticker.day?.v;
        const prevDayVolume = ticker.prevDay?.v;
        // Calculate relative volume (today's volume vs previous day)
        const relativeVolume = (todayVolume && prevDayVolume && prevDayVolume > 0)
          ? (todayVolume / prevDayVolume).toFixed(2)
          : null;

        snapshotMap[ticker.ticker] = {
          currentPrice: ticker.day?.c || ticker.prevDay?.c,
          todayOpen: ticker.day?.o,
          todayHigh: ticker.day?.h,
          todayLow: ticker.day?.l,
          previousClose: ticker.prevDay?.c,
          change: ticker.todaysChangePerc || 0,
          // Volume data
          todayVolume,
          prevDayVolume,
          relativeVolume: relativeVolume ? parseFloat(relativeVolume) : null,
          // VWAP (Volume Weighted Average Price)
          vwap: ticker.day?.vw,
          // Pre-market data
          preMarketHigh: ticker.preMarket?.h,
          preMarketLow: ticker.preMarket?.l
        };
      }
      console.log(`✅ Fetched batch snapshots for ${Object.keys(snapshotMap).length} tickers`);
      return snapshotMap;
    }
    return {};
  } catch (error) {
    console.error('Error fetching batch snapshots:', error.message);
    return {};
  }
};

// Fetch market index ETF data (SPY, QQQ, DIA, IWM)
/**
 * Fetch full market context from backend (single source of truth).
 * Backend computes 4-index weighted sentiment + IWM-SPY spread class.
 * Replaces fetchMarketIndices + client-side calculateMarketSentiment.
 * Returns null on error — caller should show explicit error state, not
 * fall back to client-side calc (defeats the source-of-truth purpose).
 */
export const fetchMarketContext = async () => {
  try {
    // Routes through apiService.apiCall: on 401, silently refreshes the
    // access token and retries before giving up. Without this delegation
    // (raw fetch path), a 401 here dispatched auth-expired directly and
    // booted the user every time the access token hit its 1-hour mark.
    return await apiGetMarketContext();
  } catch (error) {
    // apiCall already dispatches auth-expired when refresh itself fails;
    // here we only need to surface a categorised error for the UI.
    log.error('MARKET_CONTEXT_FETCH', error);
    return { errorType: classifyNetworkError(error) };
  }
};

export const fetchMarketIndices = async () => {
  const indices = [
    { symbol: 'SPY', name: 'S&P 500', indexName: 'S&P 500 Index', description: 'SPY tracks S&P 500 (500 large-cap US stocks)' },
    { symbol: 'QQQ', name: 'NASDAQ', indexName: 'Nasdaq-100 Index', description: 'QQQ tracks Nasdaq-100 (100 largest Nasdaq stocks)' },
    { symbol: 'DIA', name: 'Dow 30', indexName: 'Dow Jones Industrial Average', description: 'DIA tracks Dow Jones (30 blue-chip stocks)' },
    { symbol: 'IWM', name: 'Russell 2K', indexName: 'Russell 2000 Index', description: 'IWM tracks Russell 2000 (2000 small-cap stocks)' }
  ];

  try {
    const results = await Promise.all(
      indices.map(async ({ symbol, name, indexName, description }) => {
        const snapshot = await fetchTickerSnapshot(symbol);

        return {
          symbol,
          name,
          indexName,
          description,
          price: snapshot?.price || null,
          change: snapshot?.change || null
        };
      })
    );
    return results;
  } catch (error) {
    console.error('Error fetching market indices:', error.message);
    return [];
  }
};

// ==========================================
// Intraday Minute Bars (for Backtest Simulation)
// ==========================================

/**
 * Fetch 1-minute intraday bars for a symbol on a specific date
 * Uses Polygon /v2/aggs/ticker/{symbol}/range/1/minute/{date}/{date}
 * @param {string} symbol - Stock symbol
 * @param {string} date - Date YYYY-MM-DD
 * @returns {Array|null} - Array of bars [{t, o, h, l, c, v}, ...] or null on error
 */
export const fetchIntradayBars = async (symbol, date) => {
  try {
    const data = await fetchWithRetry(
      `https://api.polygon.io/v2/aggs/ticker/${symbol}/range/1/minute/${date}/${date}?adjusted=true&sort=asc&limit=50000&apiKey=${API_KEY}`
    );
    if (data.resultsCount > 0 && data.results) {
      return data.results;
    }
    console.warn(`No intraday bars for ${symbol} on ${date}`);
    return null;
  } catch (error) {
    console.error(`Error fetching intraday bars for ${symbol}:`, error.message);
    return null;
  }
};

/**
 * Fetch intraday bars for multiple symbols in parallel batches
 * Paid plan: 20 parallel requests, no delay needed
 * @param {Array<string>} symbols - Array of stock symbols
 * @param {string} date - Date YYYY-MM-DD
 * @param {Function} onProgress - Optional progress callback (completed, total)
 * @returns {Object} - Map of { symbol: bars[] }
 */
export const fetchBatchIntradayBars = async (symbols, date, onProgress) => {
  if (!symbols || symbols.length === 0) return {};

  const BATCH_SIZE = 20;
  const barsMap = {};
  let completed = 0;

  for (let i = 0; i < symbols.length; i += BATCH_SIZE) {
    const batch = symbols.slice(i, i + BATCH_SIZE);

    const results = await Promise.all(
      batch.map(async (symbol) => {
        const bars = await fetchIntradayBars(symbol, date);
        completed++;
        if (onProgress) onProgress(completed, symbols.length);
        return { symbol, bars };
      })
    );

    results.forEach(({ symbol, bars }) => {
      if (bars) {
        barsMap[symbol] = bars;
      }
    });
  }

  console.log(`✅ Fetched intraday bars for ${Object.keys(barsMap).length}/${symbols.length} symbols on ${date}`);
  return barsMap;
};

// Helper function to build AI analysis payload
// Fetch 52-week high/low for a batch of symbols using monthly aggregate bars
// Uses Polygon /v2/aggs/ticker/{ticker}/range/1/month/{from}/{to}
// Returns ~12 monthly bars, then computes max high and min low
export const fetch52WeekRange = async (symbols) => {
  if (!symbols || symbols.length === 0) return {};

  const to = getETDateString(); // today in ET: YYYY-MM-DD
  const fromDate = getETNow();
  fromDate.setFullYear(fromDate.getFullYear() - 1);
  const from = fromDate.toLocaleDateString('en-CA'); // 1 year ago in ET

  const BATCH_SIZE = 20;
  const DELAY_MS = 200; // small delay between batches to respect rate limits
  const rangeMap = {};

  for (let i = 0; i < symbols.length; i += BATCH_SIZE) {
    const batch = symbols.slice(i, i + BATCH_SIZE);

    const results = await Promise.all(
      batch.map(async (symbol) => {
        try {
          const data = await fetchWithRetry(
            `https://api.polygon.io/v2/aggs/ticker/${symbol}/range/1/month/${from}/${to}?adjusted=true&sort=asc&apiKey=${API_KEY}`
          );
          if (data.results && data.results.length > 0) {
            // Find max high and min low across all monthly bars
            let high = -Infinity;
            let low = Infinity;
            for (const bar of data.results) {
              if (bar.h > high) high = bar.h;
              if (bar.l < low) low = bar.l;
            }
            return { symbol, high, low };
          }
          return { symbol, high: null, low: null };
        } catch {
          return { symbol, high: null, low: null };
        }
      })
    );

    results.forEach(r => {
      rangeMap[r.symbol] = { high52w: r.high, low52w: r.low };
    });

    // Delay between batches (except last)
    if (i + BATCH_SIZE < symbols.length) {
      await new Promise(resolve => setTimeout(resolve, DELAY_MS));
    }
  }

  console.log(`✅ Fetched 52-week range for ${Object.keys(rangeMap).length} tickers`);
  return rangeMap;
};

// Fetch float (shares outstanding) for a batch of symbols using Ticker Details API
// Uses Polygon /v3/reference/tickers/{ticker}
export const fetchFloatData = async (symbols) => {
  if (!symbols || symbols.length === 0) return {};

  const BATCH_SIZE = 20;
  const DELAY_MS = 200;
  const floatMap = {};

  for (let i = 0; i < symbols.length; i += BATCH_SIZE) {
    const batch = symbols.slice(i, i + BATCH_SIZE);

    const results = await Promise.all(
      batch.map(async (symbol) => {
        try {
          const data = await fetchWithRetry(
            `${BASE_URL}/${symbol}?apiKey=${API_KEY}`
          );
          if (data.status === 'OK' && data.results) {
            return {
              symbol,
              sharesOutstanding: data.results.share_class_shares_outstanding || data.results.weighted_shares_outstanding,
            };
          }
          return { symbol, sharesOutstanding: null };
        } catch {
          return { symbol, sharesOutstanding: null };
        }
      })
    );

    results.forEach(r => {
      floatMap[r.symbol] = { float: r.sharesOutstanding };
    });

    if (i + BATCH_SIZE < symbols.length) {
      await new Promise(resolve => setTimeout(resolve, DELAY_MS));
    }
  }

  console.log(`✅ Fetched float data for ${Object.keys(floatMap).length} tickers`);
  return floatMap;
};

// Now uses batch snapshot API for accurate today's high/low
// Exported so handleAiAnalysis can call once and reuse for both scan + risk assessment
export const buildAiPayload = async (stocks, context) => {
  // Phase 2: Learning Loop - Load user profile from backend DB
  let userProfile = null;
  try {
    const profileResp = await apiGetProfile();
    if (profileResp?.profileData) {
      const insights = typeof profileResp.profileData === 'string' ? JSON.parse(profileResp.profileData) : profileResp.profileData;
      userProfile = insights.userProfile || null;
      if (userProfile) {
        console.log('✅ Loaded user profile from API:', {
          winRate: insights.quickStats?.winRate,
          totalPatterns: insights.quickStats?.totalPatterns
        });
      }
    }
  } catch (error) {
    console.log('ℹ️ No user profile available, using default analysis:', error.message);
  }

  // Fetch batch snapshots, 52-week range, and float data in parallel
  const symbols = stocks.map(s => s.symbol);
  const [snapshots, weekRange, floatData] = await Promise.all([
    fetchBatchSnapshots(symbols),
    fetch52WeekRange(symbols),
    fetchFloatData(symbols)
  ]);

  return {
    stocks: stocks.map(stock => {
      // Use snapshot data for accurate high/low/volume, fallback to stock data
      const snapshot = snapshots[stock.symbol] || {};
      const range52w = weekRange[stock.symbol] || {};
      const floatInfo = floatData[stock.symbol] || {};
      // Calculate Gap % from Open and PrevClose
      const open = snapshot.todayOpen;
      const prevClose = snapshot.previousClose || stock.previousClose;
      const gapPercent = (open && prevClose && prevClose > 0)
        ? ((open - prevClose) / prevClose * 100)
        : null;
      return {
        symbol: stock.symbol,
        name: stock.name,
        dailyGain: stock.dailyGainPercentage,
        currentPrice: stock.currentPrice || snapshot.currentPrice,
        previousClose: stock.previousClose || snapshot.previousClose,
        todayOpen: snapshot.todayOpen,
        todayHigh: snapshot.todayHigh,
        todayLow: snapshot.todayLow,
        // Volume data
        todayVolume: snapshot.todayVolume,
        relativeVolume: snapshot.relativeVolume,
        // VWAP and Pre-market data
        vwap: snapshot.vwap,
        preMarketHigh: snapshot.preMarketHigh,
        preMarketLow: snapshot.preMarketLow,
        // 52-week range
        high52w: range52w.high52w,
        low52w: range52w.low52w,
        // Float and Gap
        floatShares: floatInfo.float,
        gapPercent: gapPercent !== null ? parseFloat(gapPercent.toFixed(2)) : null,
        // Cap tier (Mega / Large / Mid / Small / Micro) — needed by the
        // UI for the Cap chip in DetailedPickCard, RiskModal, and the
        // Context Strip Row 2 mix display.
        marketCap: stock.marketCap || null,
        // Sector — sicCode (4-digit numeric, used by the UI's
        // getSectorCategory range bucketing), the raw SIC description
        // (shown verbatim in Card/Modal sublines), and the legacy
        // `sector` alias that the AI payload consumer reads.
        sicCode: stock.sicCode || null,
        sicDescription: stock.sicDescription || null,
        sector: stock.sicDescription || null,
        priceHistory: stock.priceHistory?.map(p => ({
          price: p.price,
          timestamp: p.timestamp
        })) || []
      };
    }),
    context: {
      config: context.config,
      marketStatus: context.marketStatus,
      marketSentiment: context.marketSentiment,
      indices: context.indices?.map(i => ({
        symbol: i.symbol,
        name: i.name,
        change: i.change
      })) || []
    },
    userProfile
  };
};

// Send pre-built payload to Claude (used when payload is built once and shared).
// Routes through apiService.apiCall: gains auth + silent refresh on 401.
// Default 0 retries — AI calls cost tokens and may have side effects.
export const sendClaudeAnalysis = async (payload) => {
  try {
    console.log('📤 Sending Claude analysis request:', { stockCount: payload.stocks.length });
    return await apiSendClaudeAnalysis(payload);
  } catch (error) {
    console.error('Error fetching Claude analysis:', error.message);
    return { provider: AI_PROVIDER.ANTHROPIC, error: error.message };
  }
};

// Send pre-built payload to OpenAI (used when payload is built once and shared).
export const sendOpenaiAnalysis = async (payload) => {
  try {
    console.log('📤 Sending OpenAI analysis request:', { stockCount: payload.stocks.length });
    return await apiSendOpenaiAnalysis(payload);
  } catch (error) {
    console.error('Error fetching OpenAI analysis:', error.message);
    return { provider: AI_PROVIDER.OPENAI, error: error.message };
  }
};

// Build the RiskAssessmentRequest payload. Shared by the (legacy) non-streaming
// fetchRiskAssessment and the streaming consumer in StockChartTable.
export const buildRiskPayload = (anthropicResult, openaiResult, enrichedStocks, context) => {
  const allSymbols = new Set();
  anthropicResult.topPicks?.forEach(p => allSymbols.add(p.symbol));
  openaiResult.topPicks?.forEach(p => allSymbols.add(p.symbol));
  anthropicResult.avoid?.forEach(a => allSymbols.add(a.symbol));
  openaiResult.avoid?.forEach(a => allSymbols.add(a.symbol));

  const relevantStocks = enrichedStocks.filter(s => allSymbols.has(s.symbol));

  return {
    claudeScan: {
      provider: AI_PROVIDER.ANTHROPIC,
      summary: anthropicResult.summary,
      topPicks: (anthropicResult.topPicks || []).map(p => ({
        symbol: p.symbol,
        rating: p.rating,
        trend: p.trend,
        dailyGain: p.dailyGain,
        pattern: p.pattern,
        action: p.action
      })),
      avoid: (anthropicResult.avoid || []).map(a => ({
        symbol: a.symbol,
        reason: a.reason
      }))
    },
    gptScan: {
      provider: AI_PROVIDER.OPENAI,
      summary: openaiResult.summary,
      topPicks: (openaiResult.topPicks || []).map(p => ({
        symbol: p.symbol,
        rating: p.rating,
        trend: p.trend,
        dailyGain: p.dailyGain,
        pattern: p.pattern,
        action: p.action
      })),
      avoid: (openaiResult.avoid || []).map(a => ({
        symbol: a.symbol,
        reason: a.reason
      }))
    },
    stockData: relevantStocks,
    context: context
  };
};

// Fetch Risk Assessment (Claude) - called after both scan results are available.
// Legacy non-streaming variant. Kept for now; StockChartTable uses the streaming
// path via streamRiskAssessment.
export const fetchRiskAssessment = async (anthropicResult, openaiResult, enrichedStocks, context) => {
  try {
    const payload = buildRiskPayload(anthropicResult, openaiResult, enrichedStocks, context);
    console.log('🛡️ Sending risk assessment request:', {
      claudePicks: payload.claudeScan.topPicks.length,
      gptPicks: payload.gptScan.topPicks.length,
      stockData: payload.stockData.length
    });
    return await apiFetchRiskAssessment(payload);
  } catch (error) {
    console.error('Error fetching risk assessment:', error.message);
    return { provider: AI_PROVIDER.ANTHROPIC, error: error.message };
  }
};

